import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { translation } from "../../translation";

const ProfileSwitcherBar = ({
  profiles = [],
  currentProfile,
  onSwitch,
  isSwitching = false,
  isAtBottom = false,
}) => {
  const lang = useSelector(
    (state) => state.collaborator.collaboratorInfo.language,
  );

  const [isIconAnimating, setIsIconAnimating] = useState(false);

  const switchLabel = translation.settings.switchProfile.switchProfile[lang];

  const getInitials = (firstName, lastName) => {
    const first =
      typeof firstName === "string"
        ? firstName
        : firstName?.[lang] || firstName?.en || firstName?.fr || "";
    const last =
      typeof lastName === "string"
        ? lastName
        : lastName?.[lang] || lastName?.en || lastName?.fr || "";
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const handleSwitch = useCallback(() => {
    if (isIconAnimating || isSwitching) return;

    setIsIconAnimating(true);
    onSwitch();

    window.setTimeout(() => {
      setIsIconAnimating(false);
    }, 650);
  }, [isIconAnimating, isSwitching, onSwitch]);

  const displayProfiles = profiles.slice(0, 2);

  const gradientColors = [
    "from-orange-400 to-orange-500",
    "from-blue-400 to-blue-500",
    "from-green-400 to-green-500",
  ];

  return (
    <div
      className={`fixed left-1/2 z-[100] w-[calc(100%-40px)] max-w-[500px] -translate-x-1/2 transition-all duration-300 ease-out ${
        isAtBottom ? "bottom-[100px]" : "bottom-6"
      }`}
    >
      <button
        type="button"
        onClick={handleSwitch}
        disabled={isSwitching}
        aria-label={switchLabel}
        aria-busy={isSwitching}
        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-lg transition-shadow hover:shadow-xl disabled:cursor-wait disabled:opacity-90"
      >
        {/* Profile Avatars */}
        <div className="flex flex-shrink-0 items-center -space-x-2">
          {displayProfiles.map((profile, index) => {
            const isActive = currentProfile?._id === profile._id;

            return (
              <div
                key={profile._id}
                className={`relative ${isActive ? "z-10" : "z-0"}`}
              >
                {profile.profile_picture ? (
                  <div
                    className={`h-8 w-8 overflow-hidden rounded-full border-2 shadow-md transition-all duration-300 ${
                      isActive ? "border-gray-800" : "border-white"
                    }`}
                  >
                    <img
                      src={`${process.env.REACT_APP_IMAGE_PATH}${profile.profile_picture}`}
                      alt={getInitials(profile.first_name, profile.last_name)}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 bg-gradient-to-br ${gradientColors[index % 3]} shadow-md transition-all duration-300 ${
                      isActive ? "border-gray-800" : "border-white"
                    }`}
                  >
                    <span
                      className={`text-[11px] font-bold text-white ${
                        lang === "ar" ? "!font-readexBold" : "font-SteradianMedium"
                      }`}
                    >
                      {getInitials(profile.first_name, profile.last_name)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Label */}
        <div
          className={`min-w-0 flex-1 text-center text-[13px] font-medium text-gray-800 ${
            lang === "ar" ? "!font-readexMedium" : "font-SteradianMedium"
          }`}
        >
          {switchLabel}
        </div>

        {/* Switch icon */}
        <span
          className={`profile-switch-btn-shimmer relative flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-visible rounded-full shadow-md transition-transform duration-200 hover:scale-105 active:scale-95 ${
            isSwitching || isIconAnimating
              ? ""
              : "animate-profile-switch-pulse-ring"
          }`}
        >
          {isIconAnimating && !isSwitching && (
            <span
              className="animate-profile-switch-ripple pointer-events-none absolute inset-0 rounded-full bg-gray-600"
              aria-hidden="true"
            />
          )}

          {isSwitching ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <svg
              className={`relative z-[1] h-3.5 w-3.5 text-white ${
                isIconAnimating ? "animate-profile-switch-icon-flip" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
          )}
        </span>
      </button>
    </div>
  );
};

export default ProfileSwitcherBar;
